import { Helmet } from "react-helmet";
import Layout from "@/components/Layout";

const TermsAndConditions = () => {
  return (
    <Layout>
      <Helmet>
        <title>Terms & Conditions | Dominican Sol Transfers</title>
        <meta name="description" content="Read our terms and conditions for transfer, tour, and car rental booking services in the Dominican Republic." />
      </Helmet>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-4xl font-bold mb-8">Terms & Conditions</h1>
          <p className="text-muted-foreground mb-8">Last updated: April 14, 2026</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                Welcome to Dominican Sol Transfers. These Terms and Conditions govern your use of our website and services, including airport transfers, private tours, shuttle services, car rentals, and real estate inquiries in the Dominican Republic. By using our website or booking any service, you agree to be bound by these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Services</h2>
              <p className="text-muted-foreground leading-relaxed">
                We provide transportation and tourism services including but not limited to: airport transfers to and from all major airports in the Dominican Republic (Punta Cana, Santo Domingo, Santiago, Puerto Plata, La Romana, Samaná), private and group tours, shuttle services, car rentals, and real estate listing services. All services are subject to availability and confirmation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Booking & Reservations</h2>
              <p className="text-muted-foreground leading-relaxed">
                All bookings must be made through our website or by contacting our customer service team. A booking is confirmed only after you receive a confirmation email with your booking number. We recommend booking at least 24 hours in advance. Last-minute bookings are subject to availability.
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>You must provide accurate flight information for airport transfers</li>
                <li>Passenger count must be accurate at the time of booking</li>
                <li>Special requirements must be communicated at the time of booking</li>
                <li>Children under 2 years travel free; children 2-12 may have reduced rates on tours</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Pricing & Payment</h2>
              <p className="text-muted-foreground leading-relaxed">
                All prices are displayed in US Dollars (USD) unless otherwise stated. Prices include standard service as described. Additional services, extras, or upgrades may incur additional charges. We accept payment via PayPal, credit cards, and cash (where available). Full payment is required at the time of booking unless otherwise agreed.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Cancellation & Refund Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cancellation policies vary by service type:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li><strong>Airport Transfers:</strong> Free cancellation up to 24 hours before pickup. Cancellations within 24 hours may be subject to a 50% charge.</li>
                <li><strong>Tours:</strong> Free cancellation up to 48 hours before the tour date. Cancellations within 48 hours are non-refundable.</li>
                <li><strong>Car Rentals:</strong> Free cancellation up to 48 hours before pickup. Late cancellations may forfeit the first day's rental fee.</li>
                <li><strong>No-shows:</strong> No-shows are non-refundable for all services.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Refunds will be processed within 5-10 business days through the original payment method.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Service Modifications</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify or substitute services due to unforeseen circumstances such as road conditions, weather, vehicle availability, or safety concerns. In such cases, we will provide an equivalent or superior alternative. If no suitable alternative is available, a full refund will be provided.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Passenger Responsibilities</h2>
              <p className="text-muted-foreground leading-relaxed">
                Passengers are expected to be at the designated pickup location at the agreed time. For airport pickups, our driver will wait up to 30 minutes after flight landing for domestic flights and 60 minutes for international flights. Passengers must wear seatbelts at all times. Any damage caused to vehicles by passengers will be charged accordingly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Liability Limitations</h2>
              <p className="text-muted-foreground leading-relaxed">
                Dominican Sol Transfers shall not be liable for delays caused by traffic, road conditions, weather, or other circumstances beyond our control. Our liability for any claim shall not exceed the amount paid for the specific service in question. We are not responsible for lost or damaged personal belongings left in vehicles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Insurance</h2>
              <p className="text-muted-foreground leading-relaxed">
                All our transfer vehicles carry mandatory insurance as required by Dominican Republic law. For car rentals, basic insurance is included in the rental price. Additional comprehensive coverage options are available at an extra cost. We recommend passengers carry their own travel insurance for medical emergencies and trip interruptions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                All content on this website, including text, images, logos, and design, is the property of Dominican Sol Transfers and is protected by intellectual property laws. Unauthorized reproduction or distribution is prohibited.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These terms shall be governed by and construed in accordance with the laws of the Dominican Republic. Any disputes arising from these terms shall be resolved in the courts of the Dominican Republic.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms & Conditions, please contact us at:
              </p>
              <ul className="list-none text-muted-foreground space-y-2 mt-4">
                <li><strong>Email:</strong> info@dominicansol.com</li>
                <li><strong>Phone:</strong> Available 24/7</li>
                <li><strong>Location:</strong> Punta Cana, Dominican Republic</li>
              </ul>
            </section>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TermsAndConditions;
